import { provideApolloClient } from "@vue/apollo-composable";
import { apolloClient } from "@/providers/graphql.provider";
import { ContributionConversationRound } from "@/models/contribution";
import { StreamTranscriptionClient, TranscriptionStreamResult } from "@/utils/streamTranscriptionClient";
import { CreateTranscribeStreamUrlMutation, useCreateTranscribeStreamUrlMutation } from "@/graphql/API";
import { FetchResult } from "@apollo/client";

const MAX_FOLLOWUP_QUESTIONS = 1;

export interface ConversationalSurveyQuestionUpdated {
    question: string;
}

export interface ConversationalSurveyRound {
    question: string;
    questionTimestamp: number;
    answer: string;
    answerLength: number;
}

export class ConversationalSurveyService {
    private readonly onQuestionUpdated: (resp: ConversationalSurveyQuestionUpdated) => void;
    private readonly onQuestionFinalized: () => void;
    private readonly onSurveyEnded: (rounds: ConversationalSurveyRound[]) => void;

    private readonly campaignID: string;
    private readonly streamTranscriptionClient: StreamTranscriptionClient;
    private readonly rating: string | null;
    private readonly isCoreporter: boolean;
    private readonly isCoreporterAdvanced: boolean;
    readonly rounds: ConversationalSurveyRound[];

    constructor({
        campaignID,
        initialQuestion,
        onQuestionUpdated,
        onQuestionFinalized,
        onSurveyEnded,
        rating,
        isCoreporter,
        isCoreporterAdvanced,
    }: {
        campaignID: string;
        initialQuestion: string;
        onQuestionUpdated: (resp: ConversationalSurveyQuestionUpdated) => void;
        onQuestionFinalized: () => void;
        onSurveyEnded: (rounds: ConversationalSurveyRound[]) => void;
        rating: string | null;
        isCoreporter: boolean;
        isCoreporterAdvanced: boolean;
    }) {
        provideApolloClient(apolloClient);

        this.onQuestionUpdated = onQuestionUpdated;
        this.onQuestionFinalized = onQuestionFinalized;
        this.onSurveyEnded = onSurveyEnded;
        this.campaignID = campaignID;
        this.rating = rating;
        this.isCoreporter = isCoreporter;
        this.isCoreporterAdvanced = isCoreporterAdvanced;

        this.rounds = [
            {
                question: initialQuestion,
                questionTimestamp: 0,
                answer: "",
                answerLength: 0,
            },
        ];

        this.streamTranscriptionClient = new StreamTranscriptionClient({
            transcribeStreamURLFetcher: () => {
                return this.createTranscribeStreamURL();
            },
        });
    }

    async listenToNextAnswerAndRespond(rawStream: MediaStream): Promise<void> {
        await this.streamTranscriptionClient.registerStream(rawStream);

        this.streamTranscriptionClient.iterator().then((readableStream) => {
            return this.subscribeToTranscriptStream(readableStream);
        });
    }

    async stopListening(timestamp: number): Promise<void> {
        await this.streamTranscriptionClient.stopStream();
        this.rounds[this.rounds.length - 1].answerLength = timestamp;
        this.prepareForNextQuestion(timestamp);
    }

    getNextQuestion(answer: string) {
        this.rounds[this.rounds.length - 1].answer = answer;
        this.prepareForNextQuestion(undefined);
    }

    private prepareForNextQuestion(timestamp: number | undefined) {
        const maxQuestions = this.isCoreporter ? 3 : MAX_FOLLOWUP_QUESTIONS;
        if (this.rounds.length === maxQuestions + 1) {
            // +1 because we always have the initial question
            console.log("Reached max amount of follow up questions. Ending survey.");
            this.onSurveyEnded(this.rounds);
            return;
        }

        this.subscribeToFollowupQuestion({
            conversation: this.rounds.map((round) => {
                return {
                    question: round.question,
                    transcript: round.answer || "{User provided no answer}",
                };
            }),
        });

        this.rounds.push({
            question: "",
            questionTimestamp: timestamp || 0,
            answer: "",
            answerLength: 0,
        });
    }

    private subscribeToFollowupQuestion({ conversation }: { conversation: ContributionConversationRound[] }) {
        const socket = new WebSocket(`wss://${process.env.VUE_APP_API_WEBSOCKETS_DOMAIN}`);

        const messages: { isAssistant: boolean; text: string | undefined }[] = [];
        conversation.forEach((message) => {
            messages.push({
                isAssistant: true,
                text: message.question,
            });

            if (this.rating) {
                messages.push({
                    isAssistant: false,
                    text: `${message.transcript} ${this.rating}`,
                });
            } else {
                messages.push({
                    isAssistant: false,
                    text: message.transcript,
                });
            }
        });

        socket.onopen = () => {
            console.log("[open] Connection established");
            console.log("Sending to server");

            const payload = {
                action: this.isCoreporter ? "coreporter" : "conversationalSurvey",
                campaignID: this.campaignID,
                messages,
                useAdvanced: !!this.isCoreporterAdvanced,
            };
            console.log(payload);

            socket.send(JSON.stringify(payload));
        };

        socket.onmessage = (event) => {
            if (!event.data) {
                return;
            }
            this.rounds[this.rounds.length - 1].question += event.data;
            this.onQuestionUpdated({ question: this.rounds[this.rounds.length - 1].question });
        };

        socket.onclose = (event) => {
            if (event.wasClean) {
                console.log(`[close] Connection closed cleanly, code=${event.code} reason=${event.reason}`);
            } else {
                // e.g. server process killed or network down
                // event.code is usually 1006 in this case
                console.log(`[close] Connection died, code=${event.code} reason=${event.reason}`);
            }
            this.onQuestionFinalized();

            if (this.rounds[this.rounds.length - 1].question === "<!STOP!>") {
                console.log("STOP RECEIVED FROM AGENT, ending survey");
                this.onSurveyEnded(this.rounds);
            }
        };

        socket.onerror = (event) => {
            console.error(`[error]`, event);
            this.onSurveyEnded(this.rounds);
        };
    }

    private async subscribeToTranscriptStream(stream: AsyncIterable<TranscriptionStreamResult>) {
        for await (const { transcriptText, isPartial } of stream) {
            if (isPartial) {
                continue;
            }

            this.rounds[this.rounds.length - 1].answer += transcriptText;
        }
    }

    private async createTranscribeStreamURL(): Promise<string> {
        const { mutate } = useCreateTranscribeStreamUrlMutation({
            variables: {
                campaignID: this.campaignID,
            },
        });

        const mutationResponse: FetchResult<CreateTranscribeStreamUrlMutation> | null = await mutate();
        if (!mutationResponse || !mutationResponse.data || !mutationResponse.data.createTranscribeStreamURL) {
            throw new Error("Failed to create transcribe stream URL");
        }

        return mutationResponse.data.createTranscribeStreamURL.url;
    }
}
